'use client'
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react'
import { getUserProfile, User } from '@/lib/crate-core'
import { clearCredentials, getCredentials } from '@/lib/utils'

// create context
const UserContext = createContext<{
  user?: User
  userToken?: string
  isAuthenticating: boolean
  login: (username: string, password: string) => void
  logout: () => void
}>({
  user: undefined,
  userToken: undefined,
  isAuthenticating: false,
  login: () => {},
  logout: () => {},
})

const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | undefined>(undefined)
  const [userToken, setUserToken] = useState<string | undefined>(undefined)
  const [isAuthenticating, setIsAuthenticating] = useState(true)

  const login = useCallback((username: string, password: string) => {
    throw new Error('Not implemented')
  }, [])

  const logout = useCallback(() => {
    throw new Error('Not implemented')
  }, [])

  useEffect(() => {
    console.debug('Getting user profile...')

    const abortController = new AbortController()

    const fn = async () => {
      // If we have no credentials, abort
      const { accessToken } = getCredentials()

      if (!accessToken) {
        console.debug('No credentials found!')
        return null
      }

      return await getUserProfile({ signal: abortController.signal })
    }

    setIsAuthenticating(true)

    fn()
      .then((r) => {
        if (!r) {
          console.debug('No user found!')
          return null
        }

        console.debug('Welcome, %s!', r.email)
        // Always update user token state
        const { accessToken } = getCredentials()
        setUserToken(accessToken)
        setUser(r)
      })
      .catch((e) => {
        console.error('Error getting user profile', e)

        // Handle unauthenticated user
        if (e.response?.status === 401) {
          console.debug('User is not authenticated, clearing credentials')
          clearCredentials()
        }
      })
      .finally(() => setIsAuthenticating(false))

    return () => {
      abortController.abort('Component unmounted')
    }
  }, [])

  return (
    // the Provider gives access to the context to its children
    <UserContext.Provider
      value={{
        user,
        userToken,
        isAuthenticating,
        login,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

const useUserContext = () => useContext(UserContext)

export { useUserContext, UserContextProvider }
